import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack, Grid, GridItem, Hide} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotString} from 'ramda-adjunct';

import {GetAuthorizationProfilesResponse} from '@dms/api/security';
import {GetBranchListApiResponse} from '@dms/api/shared';
import {
  GetTransferOrderResponse,
  GetWarehousesResponse,
  useLazyGetWarehouseQuery,
  usePatchTransferOrderMutation,
  usePostTransferOrderMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getOptionsFromWarehouses,
  getOptionsFromAuthorizationProfiles,
  handleApiError,
  logError,
  getOptionsFromBranches,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, Nullish, useNavigate} from 'shared/web';

import {
  BasicInformationFormDefinition,
  BasicInformationFormDefinitionSchema,
} from '../types/BasicInformationFormDefinition';

interface BasicInformationFormProps {
  transferOrder: GetTransferOrderResponse | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  branches: GetBranchListApiResponse | Nullish;
  authorizationProfiles: GetAuthorizationProfilesResponse[] | Nullish;
  onClose: VoidFunction;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const {transferOrderVariantId, transferOrderId} = useWarehouseParams();

  const navigate = useNavigate();

  const isDraft = isNilOrEmpty(transferOrderId);

  const [getWarehouse, warehouseStatus] = useLazyGetWarehouseQuery();

  const [postTransferOrder] = usePostTransferOrderMutation();

  const [patchTransferOrder] = usePatchTransferOrderMutation();

  const defaultValues: Partial<BasicInformationFormDefinition> = {
    sourceWarehouseId: props.transferOrder?.sourceWarehouseId,
    destinationWarehouseId: props.transferOrder?.destinationWarehouseId,
    destinationBranchId: props.transferOrder?.destinationBranchId,
    destinationAuthorizationProfileId: props.transferOrder?.destinationAuthorizationProfileId,
    note: props.transferOrder?.note,
  };

  const handleSubmit: FormSubmitHandler<BasicInformationFormDefinition> = async (formValues) => {
    if (isDraft) {
      if (isNilOrEmpty(transferOrderVariantId)) {
        return logError('transferOrderVariantId is not defined');
      }

      return await postTransferOrder({
        transferOrderVariantId,
        sourceWarehouseId: formValues.sourceWarehouseId,
        destinationWarehouseId: formValues.destinationWarehouseId,
        destinationBranchId: formValues.destinationBranchId,
        destinationAuthorizationProfileId: formValues.destinationAuthorizationProfileId,
        note: formValues.note,
      })
        .unwrap()
        .then((result) =>
          navigate(
            composePath(warehouseRoutes.transferOrderDetailOverview, {
              params: {id: result.transferOrderId},
            })
          )
        )
        .then(() => showNotification.success(i18n.t('general.notifications.successfullyCreated')))
        .catch(handleApiError);
    }

    if (isNil(props.transferOrder?.transferOrderId)) {
      return logError('transferOrderId is not defined');
    }

    await patchTransferOrder({
      transferOrderId: props.transferOrder.transferOrderId,
      body: {
        sourceWarehouseId: formValues.sourceWarehouseId,
        destinationWarehouseId: formValues.destinationWarehouseId,
        destinationBranchId: formValues.destinationBranchId,
        destinationAuthorizationProfileId: formValues.destinationAuthorizationProfileId,
        note: formValues.note,
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  return (
    <Form<BasicInformationFormDefinition>
      experimentalZodSchema={BasicInformationFormDefinitionSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const handleWarehouseChange = (value: string | number | null) => {
          if (isNil(value) || isNotString(value)) {
            return;
          }
          getWarehouse({warehouseId: value as string})
            .unwrap()
            .then((warehouse) => {
              formApi.setValue('destinationBranchId', warehouse.branchId, {shouldValidate: true});
              formApi.setValue(
                'destinationAuthorizationProfileId',
                warehouse.authorizationProfileId,
                {shouldValidate: true}
              );
            })
            .catch(handleApiError);
        };

        return (
          <VStack spacing={4}>
            <Grid columns={4}>
              <FormField
                isRequired
                isNotClearable
                control={control}
                name="sourceWarehouseId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.fromWarehouse')}
                options={getOptionsFromWarehouses(props.warehouses)}
              />
              <FormField
                isRequired
                isNotClearable
                control={control}
                name="destinationWarehouseId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.destinationWarehouse')}
                options={getOptionsFromWarehouses(props.warehouses)}
                onChange={(value) => handleWarehouseChange(value)}
              />
              <FormField
                isDisabled
                isLoading={warehouseStatus.isLoading}
                control={control}
                name="destinationAuthorizationProfileId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.destinationAuthorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(
                  props.authorizationProfiles,
                  formApi.watch('destinationAuthorizationProfileId')
                )}
              />
              <FormField
                isDisabled
                isLoading={warehouseStatus.isLoading}
                control={control}
                name="destinationBranchId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.destinationBranch')}
                options={getOptionsFromBranches(props.branches)}
              />

              <GridItem span={4}>
                <FormField
                  control={control}
                  name="note"
                  type="textarea"
                  label={i18n.t('general.labels.note')}
                />
              </GridItem>
            </Grid>
            <ButtonGroup align="right">
              <Hide when={isDraft}>
                <Button
                  variant="secondary"
                  title={i18n.t('general.labels.discard')}
                  onClick={props.onClose}
                />
              </Hide>
              <FormButton
                control={control}
                type="submit"
                title={
                  isDraft ? i18n.t('general.actions.create') : i18n.t('general.actions.saveChanges')
                }
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
